import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ACCEPT = ".csv,.txt,.tsv,.xlsx,.xlsm,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Lead import dialog — CSV / Excel with Standard format or Map columns.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {{ id: string, name?: string } | null} props.list
 * @param {() => void} [props.onImported]
 */
export default function LeadImportDialog({ open, onOpenChange, list, onImported }) {
  const [mode, setMode] = useState("standard"); // standard | map
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setMode("standard");
    setFile(null);
    setPreview(null);
    setMapping({});
    setPreviewing(false);
    setImporting(false);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const fields = preview?.fields || [
    { key: "phone", label: "Phone", required: true },
    { key: "first_name", label: "First name", required: false },
    { key: "last_name", label: "Last name", required: false },
    { key: "name", label: "Full name", required: false },
    { key: "email", label: "Email", required: false },
    { key: "phone_code", label: "Phone / dial code", required: false },
  ];

  const columns = preview?.columns || [];

  const effectiveMapping = useMemo(() => {
    if (mode === "standard") {
      return preview?.suggested_mapping || mapping;
    }
    return mapping;
  }, [mode, preview, mapping]);

  const phoneMapped = Boolean(effectiveMapping?.phone);

  const runPreview = async (f) => {
    if (!f) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/customer-leads/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const suggested = data.suggested_mapping || {};
      setMapping(suggested);
      if (data.standard_ready) {
        setMode("standard");
      } else {
        setMode("map");
        toast.message("Could not auto-detect Phone — map columns below");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const onPickFile = (f) => {
    setFile(f || null);
    if (f) runPreview(f);
    else {
      setPreview(null);
      setMapping({});
    }
  };

  const setFieldMap = (fieldKey, header) => {
    setMapping((prev) => ({
      ...prev,
      [fieldKey]: header === "__none__" ? null : header,
    }));
  };

  const doImport = async () => {
    if (!list?.id) {
      toast.error("Select a list first");
      return;
    }
    if (!file) {
      toast.error("Choose a CSV or Excel file");
      return;
    }
    if (!phoneMapped) {
      toast.error("Map a Phone column before importing");
      setMode("map");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("list_id", list.id);
      fd.append("mode", mode);
      fd.append("idempotency_key", `list-${list.id}-${Date.now()}`);
      // Always send mapping so standard mode still uses detected headers
      const mapPayload = {};
      fields.forEach((f) => {
        const v = effectiveMapping?.[f.key];
        if (v) mapPayload[f.key] = v;
      });
      fd.append("column_map", JSON.stringify(mapPayload));
      const { data } = await api.post("/customer-leads/import/file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Imported ${data.imported}`
        + (data.skipped ? ` · skipped ${data.skipped}` : "")
        + (data.rejected ? ` · rejected ${data.rejected}` : ""),
      );
      if (data.errors?.length) {
        toast.message(`${data.errors.length} row error(s) — check the first few rows`);
      }
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setImporting(false);
    }
  };

  const previewHeaders = columns.slice(0, 6);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="lead-import-dialog">
        <DialogHeader>
          <DialogTitle>Import leads{list?.name ? ` into ${list.name}` : ""}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>File (CSV or Excel)</Label>
            <Input
              type="file"
              accept={ACCEPT}
              onChange={(e) => onPickFile(e.target.files?.[0] || null)}
              data-testid="lead-import-file"
            />
            <p className="text-[11px] text-muted-foreground">
              Accepts .csv, .xlsx, .xls. First row must be column headers. Max ~50,000 rows.
            </p>
            {previewing && <p className="text-xs text-muted-foreground">Reading file…</p>}
            {preview && (
              <p className="text-xs text-muted-foreground" data-testid="lead-import-preview-meta">
                {preview.filename} · {preview.row_count} row{preview.row_count === 1 ? "" : "s"} · {columns.length} column{columns.length === 1 ? "" : "s"}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Import mode</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("standard")}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  mode === "standard" ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                }`}
                data-testid="lead-import-mode-standard"
              >
                <p className="text-sm font-semibold">Standard format</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Auto-match columns named phone, first_name, last_name, email, name, etc.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setMode("map")}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  mode === "map" ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                }`}
                data-testid="lead-import-mode-map"
              >
                <p className="text-sm font-semibold">Map columns</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Choose which file column is Phone, Name, Email, and so on.
                </p>
              </button>
            </div>
          </div>

          {mode === "standard" && preview && (
            <div className="rounded-lg border bg-muted/30 p-3 text-xs space-y-1" data-testid="lead-import-standard-map">
              <p className="font-medium text-sm mb-1">Detected mapping</p>
              {fields.map((f) => (
                <div key={f.key} className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{f.label}{f.required ? " *" : ""}</span>
                  <span className="font-mono truncate max-w-[60%] text-right">
                    {effectiveMapping?.[f.key] || "—"}
                  </span>
                </div>
              ))}
              {!phoneMapped && (
                <p className="text-destructive mt-2">
                  Phone not detected — switch to Map columns.
                </p>
              )}
            </div>
          )}

          {mode === "map" && (
            <div className="space-y-3 rounded-lg border p-3" data-testid="lead-import-map-fields">
              <p className="text-xs text-muted-foreground">
                Map each WACalls field to a column from your file. Phone is required.
              </p>
              {!preview && (
                <p className="text-xs text-muted-foreground">Upload a file to see columns.</p>
              )}
              {fields.map((f) => (
                <div key={f.key} className="grid grid-cols-2 gap-2 items-center">
                  <Label className="text-sm">
                    {f.label}{f.required ? " *" : ""}
                  </Label>
                  <Select
                    value={mapping[f.key] || "__none__"}
                    onValueChange={(v) => setFieldMap(f.key, v)}
                    disabled={!columns.length}
                  >
                    <SelectTrigger data-testid={`lead-import-map-${f.key}`}>
                      <SelectValue placeholder="Select column" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Not mapped —</SelectItem>
                      {columns.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          {preview?.sample_rows?.length > 0 && (
            <div className="space-y-1.5" data-testid="lead-import-sample">
              <Label>Preview (first rows)</Label>
              <div className="rounded-md border overflow-x-auto max-h-40">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {previewHeaders.map((h) => (
                        <TableHead key={h} className="text-[11px] whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.sample_rows.slice(0, 5).map((row, idx) => (
                      <TableRow key={idx}>
                        {previewHeaders.map((h) => (
                          <TableCell key={h} className="text-[11px] max-w-[8rem] truncate">
                            {row[h] || ""}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="rounded-lg"
            onClick={doImport}
            disabled={importing || previewing || !file || !phoneMapped}
            data-testid="lead-import-submit"
          >
            {importing ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
